import { DOCUMENT, isPlatformBrowser } from '@angular/common';
import { inject, PLATFORM_ID, Renderer2, RendererFactory2 } from '@angular/core';

export abstract class ThemeService<T> {
  isBrowser = false;

  protected abstract _currentTheme: T;

  protected _styleElement?: HTMLLinkElement;
  protected _renderer2: Renderer2;

  private _hash?: string;

  private readonly _rendererFactory: RendererFactory2 = inject(RendererFactory2);
  protected readonly _document: Document = inject<Document>(DOCUMENT);
  private readonly _platformId: Object = inject<Object>(PLATFORM_ID);

  constructor() {
    this.isBrowser = isPlatformBrowser(this._platformId);
    this._renderer2 = this._rendererFactory.createRenderer(null, null);
  }

  get currentTheme(): T {
    return this._currentTheme;
  }

  get currentThemeStr(): string {
    return this._currentTheme as string;
  }

  loadTheme(theme?: T): boolean {
    const themeFromHostname = this.getThemeByHostname();

    const calculatedTheme = theme || themeFromHostname;
    if (calculatedTheme) {
      this._currentTheme = calculatedTheme;

      if (this.isBrowser) {
        this._renderer2.addClass(this._document.documentElement, this.currentThemeStr);
        this.loadCss(this._currentTheme);
      }
      return true;
    }

    console.warn('Could not calculate theme to load');
    return false;
  }

  removeUnusedTheme(theme: string): void {
    this._renderer2.removeClass(this._document.documentElement, theme);
    if (this._styleElement) {
      this._renderer2.removeChild(this._document.head, this._styleElement);
    }
  }

  getThemeByHostname(): T | undefined {
    try {
      const url = new URL(this._document.location.href);
      let domain: string = url.hostname.replace(/(https?:\/\/)?(www.)?/i, '');
      if (domain.indexOf('/') !== -1) {
        domain = domain.split('/')[0];
      }
      return domain !== 'localhost' ? (domain.split('.')[0] as T) : undefined;
    } catch (e) {
      return undefined;
    }
  }

  loadCss(themeToLoad: T): void {
    if (this._styleElement && this.currentThemeStr) {
      this.removeUnusedTheme(this.currentThemeStr);
    }
    const themeStyleSheet = Array.from(this._document.documentElement.querySelectorAll('link[rel="stylesheet"]')).find(
      (element) => element.getAttribute('href')?.includes(this._currentTheme as string),
    );
    if (themeStyleSheet) {
      this._hash = themeStyleSheet.getAttribute('href')?.split('/').pop()?.split('.')[1];
    }

    const themeFileName = this._hash === 'css' || !this._hash ? themeToLoad : `${themeToLoad}.${this._hash}`;

    this._styleElement = this._renderer2.createElement('link') as HTMLLinkElement;
    this._renderer2.appendChild(this._document.head, this._styleElement);
    this._renderer2.setProperty(this._styleElement, 'rel', 'stylesheet');
    this._renderer2.setProperty(this._styleElement, 'href', `${themeFileName}.css`);
    this._currentTheme = themeToLoad;
  }
}
